// ─── Iter 62fs — Livraison rapide d'une vidéo ───────────────────────────────
// Composant utilisé dans la modale de détail d'une tâche vidéo (DetailSidebar).
// Pour les tâches SANS Frame.io activé : le monteur colle un lien générique
// (Google Drive, YouTube, Frame.io, ou n'importe quel lien HTTPS) + appuie
// sur "Vidéo terminée". La vidéo passe automatiquement en stage="to_post" :
//   - preview_url est rempli depuis le lien (backend auto-fallback)
//   - poster_id est pré-rempli depuis project.poster_id (backend auto-lookup)
//   - date de post calculée en +7j (frontend : rattrapage auto)
//   - webhook ASA Client déclenché avec preview_url + frameio_link + drive_link
import { useState } from 'react';
import { CheckCircle2, Link2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

export function QuickDeliverySection({ task, onDelivered }) {
  const [link, setLink] = useState(task.frameio_link || task.drive_link || task.preview_url || '');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!!(task.frameio_link || task.preview_url));

  const deliver = async () => {
    const url = (link || '').trim();
    if (!url.startsWith('http')) {
      toast.error('Colle un lien HTTPS valide (Drive, Frame, YouTube, etc.)');
      return;
    }
    // Calcule la date de post : max(demain, due_date + 7j)
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    let postDate;
    const dueIso = (task.due_date || '').slice(0, 10);
    if (dueIso) {
      const expectedPost = new Date(`${dueIso}T00:00:00`);
      expectedPost.setDate(expectedPost.getDate() + 7);
      postDate = (expectedPost > tomorrow ? expectedPost : tomorrow).toISOString().slice(0, 10);
    } else {
      const d = new Date(today); d.setDate(d.getDate() + 7);
      postDate = d.toISOString().slice(0, 10);
    }
    setBusy(true);
    try {
      const r = await api.patch(`/tasks/${task.id}`, {
        stage: 'to_post',
        frameio_link: url, // on utilise frameio_link comme champ "lien de livraison" générique
        preview_url: url,
        scheduled_post_date: postDate,
      });
      toast.success('Vidéo passée en « À poster » · ASA Client notifié');
      onDelivered?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la livraison');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="quick-delivery-toggle"
        onClick={() => setOpen(true)}
        style={{
          marginTop: 10, marginBottom: 14, width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
          background: 'transparent', border: '1px dashed rgba(245,197,106,0.35)', color: 'var(--t2)',
          fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
        }}
      >
        <CheckCircle2 size={15} color="#F5C56A" />
        Livraison rapide
        <span style={{ fontWeight: 500, fontSize: 11, color: 'var(--t3)' }}>· optionnel — colle ton lien de rendu</span>
        <ChevronDown size={14} style={{ marginLeft: 'auto' }} />
      </button>
    );
  }

  return (
    <div
      data-testid="quick-delivery-section"
      style={{
        marginTop: 10, marginBottom: 14, padding: 14, borderRadius: 12,
        background: 'linear-gradient(145deg, rgba(245,197,106,0.08) 0%, rgba(217,154,66,0.04) 100%)',
        border: '1px solid rgba(245,197,106,0.28)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <CheckCircle2 size={16} color="#F5C56A" />
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: 'var(--t1)' }}>
          Livraison rapide
        </div>
        <div style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5, color: 'var(--t3)' }}>
          · optionnel · Drive, Frame, YouTube, autre
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px', display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px', background: 'var(--sf2)', border: '1px solid var(--bd)', borderRadius: 8 }}>
          <Link2 size={13} color="var(--t3)" />
          <input
            data-testid="quick-delivery-link"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="Colle le lien de la vidéo finale…"
            style={{
              flex: 1, padding: '9px 0', background: 'transparent', border: 'none',
              fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)', outline: 'none',
            }}
          />
        </div>
        <button
          data-testid="quick-delivery-submit"
          onClick={deliver}
          disabled={busy || !(link || '').trim().startsWith('http')}
          style={{
            padding: '9px 14px', borderRadius: 8,
            background: !busy && (link || '').trim().startsWith('http')
              ? 'linear-gradient(180deg, #F5C56A 0%, #D99A42 100%)'
              : 'var(--sf2)',
            color: !busy && (link || '').trim().startsWith('http') ? '#0B1330' : 'var(--t3)',
            border: 'none',
            cursor: !busy && (link || '').trim().startsWith('http') ? 'pointer' : 'not-allowed',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
            display: 'flex', alignItems: 'center', gap: 6,
            boxShadow: !busy && (link || '').trim().startsWith('http')
              ? '0 8px 20px -6px rgba(245,197,106,0.5)'
              : 'none',
          }}
        >
          <CheckCircle2 size={14} /> {busy ? 'Livraison…' : 'Vidéo terminée'}
        </button>
      </div>
      <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: 'var(--t4)', marginTop: 8, lineHeight: 1.4 }}>
        La vidéo passe en « À poster », le posteur du projet est notifié avec la date (+7 jours), et ASA Client reçoit la preview automatiquement.
      </div>
    </div>
  );
}

export default QuickDeliverySection;
